import * as React from 'react';
import cn from 'classnames';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai/utils';

import { chatOpenAtom, chatOpenDefaultedAtom } from 'src/shared/chat';
import { chatMaximizedAtom } from 'src/shared/chatPreferences';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { config } from 'src/utils/config';
import { ChatSidebar } from 'src/chat';
import { CHAT_RESERVED_CLASS, SHELL_OVERLAY_TARGET_ID } from 'src/hooks/useShellOverlay';

import AppHeader from '../AppHeader';
import AppSidebar from '../AppSidebar';
import PageTitle from '../PageTitle';
import FeatureAvailabilityProvider from '../FeatureAvailabilityProvider';
import BillingBanner from '../BillingBanner';
import CreditAlertBanner from '../CreditAlertBanner';

import styles from './AppShell.module.scss';

type AppShellProps = {
  /**
   * Optional page-title rendered just inside the content pane. Used by
   * the admin settings layout, which previously called
   * `<PageTitle title="Settings"/>` inside the layout wrapper.
   */
  title?: string;
};

/**
 * Single dashboard layout: top header, left sidebar, scrollable content,
 * and the existing ChatSidebar on the right. Replaces both
 * `src/layouts/default` and `src/layouts/admin`.
 */
export const AppShell: React.FC<AppShellProps> = ({ title, children }) => {
  const [chatOpen, setChatOpen] = useAtom(chatOpenAtom);
  const [chatOpenDefaulted, setChatOpenDefaulted] = useAtom(chatOpenDefaultedAtom);
  const chatMaximized = useAtomValue(chatMaximizedAtom);
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const aiAddonEnabled =
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled;
  // The AI Agent is a built-in feature: mount the panel whenever chat is
  // enabled for this deployment, regardless of the account addon. The panel
  // renders a read-only "Not enabled" state when the addon is missing or the
  // backend API is unavailable (handled inside ChatSidebar).
  const chatEnabled = config.chatEnabled;
  const chatVisible = chatEnabled && chatOpen;

  // Open chat by default once per browser — only for accounts that actually
  // have the addon, so we don't force a read-only panel open for others.
  React.useEffect(() => {
    if (chatEnabled && aiAddonEnabled && !chatOpenDefaulted) {
      setChatOpen(true);
      setChatOpenDefaulted(true);
    }
  }, [chatEnabled, aiAddonEnabled, chatOpenDefaulted, setChatOpen, setChatOpenDefaulted]);

  // Mirror `.shellChatOpen` onto <html> so overlays portaled to <body>
  // (BaseModal dialogs) can inset off the panel — they live outside this
  // subtree and cannot inherit a class-scoped custom property.
  React.useEffect(() => {
    const reserved = chatVisible && !chatMaximized;
    document.documentElement.classList.toggle(CHAT_RESERVED_CLASS, reserved);
    return () => document.documentElement.classList.remove(CHAT_RESERVED_CLASS);
  }, [chatVisible, chatMaximized]);

  return (
    <div
      className={cn(styles.shell, {
        [styles.shellChatOpen]: chatVisible && !chatMaximized,
        [styles.shellChatMaximized]: chatVisible && chatMaximized,
      })}
      data-test="app-shell"
    >
      <FeatureAvailabilityProvider />
      <BillingBanner />
      <CreditAlertBanner />
      <AppHeader />
      <div className={styles.body}>
        <AppSidebar />
        <div className={styles.contentArea} id={SHELL_OVERLAY_TARGET_ID}>
          <main className={styles.content} id="appShellContent">
            {title && <PageTitle title={title} className={styles.pageTitle} />}
            {children}
          </main>
        </div>
      </div>
      {chatEnabled && <ChatSidebar />}
    </div>
  );
};

export default AppShell;
